import type { SVGProps } from "react";

/**
 * 图标集。
 *
 * 手写内联 SVG，不引图标库：原型里的图标是逐条挑过的，且数量不多；引一个
 * 图标库只会带来一个要跟着升级的依赖和一套用不上的命名空间。
 *
 * 一律 `stroke="currentColor"` / `fill="currentColor"`，由外层文字颜色决定
 * 颜色 —— 「已读」标签、导航选中态都靠它跟着变。
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base({ size = 20, ...rest }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    ...rest,
  };
}

export function HomeIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.5 10.2 12 3.4l8.5 6.8V19a1.6 1.6 0 0 1-1.6 1.6H5.1A1.6 1.6 0 0 1 3.5 19z" />
      <path d="M9.4 20.6v-7.2h5.2v7.2" />
    </svg>
  );
}

export function HeartIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 20.3S3.4 15.2 3.4 8.9A4.6 4.6 0 0 1 8 4.2c1.6 0 3 .8 4 2 1-1.2 2.4-2 4-2a4.6 4.6 0 0 1 4.6 4.7c0 6.3-8.6 11.4-8.6 11.4z" />
    </svg>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v2M12 19.2v2M2.8 12h2M19.2 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M18.5 5.5l-1.4 1.4M6.9 17.1l-1.4 1.4" />
    </svg>
  );
}

export function RecallIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 7v5l3.6 2.1" />
      <path d="M12 3.2a8.8 8.8 0 1 1-6.2 2.6" />
      <path d="M3.5 3.5v4h4" />
    </svg>
  );
}

export function PencilIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M17.5 3.5 20.5 6.5 8.6 18.4l-4.1 1 1-4.1z" />
      <path d="M4 20.5h16" />
    </svg>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M15 5 8 12l7 7" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="m9 5 7 7-7 7" />
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6 6 18 18M18 6 6 18" />
    </svg>
  );
}

export function MoreIcon(props: IconProps) {
  return (
    <svg {...base(props)} viewBox="0 0 24 24" stroke="none" fill="currentColor">
      <circle cx="5.5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="18.5" cy="12" r="1.8" />
    </svg>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="4.2" y="10.4" width="15.6" height="10.2" rx="2.4" />
      <path d="M7.6 10.4V7.9A4.4 4.4 0 0 1 12 3.5a4.4 4.4 0 0 1 4.4 4.4v2.5" />
    </svg>
  );
}

export function ReplyIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.5 6.8c0-.9.7-1.6 1.6-1.6h13.8c.9 0 1.6.7 1.6 1.6v8.4c0 .9-.7 1.6-1.6 1.6H11l-5.1 3.7v-3.7H5.1c-.9 0-1.6-.7-1.6-1.6z" />
    </svg>
  );
}

export function SendIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.5 12 20.5 4.5 13 20.5l-3-7z" />
      <path d="m10 13.5 10.5-9" />
    </svg>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="m5 12.5 4.6 4.5L19 6.5" />
    </svg>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 7h16M9.5 7V4.8h5V7M6.5 7l1 13h9l1-13" />
    </svg>
  );
}

export function ShareIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="6.5" cy="18" r="2.6" />
      <circle cx="17.5" cy="12" r="2.6" />
      <path d="m9 16.6 6-3.2" />
    </svg>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c0-4.1 3.4-6.5 7.5-6.5s7.5 2.4 7.5 6.5" />
    </svg>
  );
}

export function KeyIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="8" cy="15" r="3.5" />
      <path d="m10.6 12.6 8-8M16 5.2l2.8 2.8M13.6 7.6l2.8 2.8" />
    </svg>
  );
}

export function LogoutIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M14 4.5h3.9c.9 0 1.6.7 1.6 1.6v11.8c0 .9-.7 1.6-1.6 1.6H14" />
      <path d="M10 16.5 14.5 12 10 7.5M14.5 12H4.5" />
    </svg>
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 4v10m0 0 3.5-3.5M12 14l-3.5-3.5" />
      <path d="M4.5 16v2.5c0 .6.5 1.1 1.1 1.1h12.8c.6 0 1.1-.5 1.1-1.1V16" />
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="3.5" y="5" width="17" height="16" rx="2.4" />
      <path d="M3.5 9.5h17M8 2.8v4M16 2.8v4" />
    </svg>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 3.2a5.6 5.6 0 0 1 5.6 5.6v4.4l1.6 3H4.8l1.6-3V8.8A5.6 5.6 0 0 1 12 3.2z" />
      <path d="M9.6 19.6c.3.9 1.2 1.4 2.4 1.4s2.1-.5 2.4-1.4" />
    </svg>
  );
}

export function ImageIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.4" />
      <circle cx="9" cy="9.5" r="1.8" />
      <path d="m4.5 17.5 4.6-4.4 3.4 3.1 3-3 4 4.3" />
    </svg>
  );
}

export function RefreshIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M20 11.5A8 8 0 0 0 6 6.2L4 8" />
      <path d="M4 4v4.2h4.2" />
      <path d="M4 12.5A8 8 0 0 0 18 17.8l2-1.8" />
      <path d="M20 20v-4.2h-4.2" />
    </svg>
  );
}

export function DeviceIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="6.5" y="2.8" width="11" height="18.4" rx="2.4" />
      <path d="M10.5 18.3h3" />
    </svg>
  );
}
